import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, CheckCircle2, Circle, Save, FileText, ImageIcon, Trash2, ExternalLink } from "lucide-react";
import api, { API_BASE } from "../lib/api";
import Avatar from "../components/Avatar";
import FileUpload from "../components/FileUpload";
import CommentThread from "../components/CommentThread";
import { useAuth } from "../context/AuthContext";
import { formatAED, formatDate, formatFileSize, relativeTime } from "../lib/format";

const STATUSES = ["active", "delivered", "invoiced", "paid"];

export default function DealDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [newMs, setNewMs] = useState({ title: "", due_date: "" });
  const [groupNotes, setGroupNotes] = useState("");
  const [privateNotes, setPrivateNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/deals/${id}`);
    setDeal(data);
    setGroupNotes(data.group_notes || "");
    setPrivateNotes(data.private_notes || "");
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!deal) return <div className="text-[#9A9590]">Loading…</div>;

  const isCoordinator = deal.coordinator_id === user.id;
  const isContributor = (deal.contributing_member_ids || []).includes(user.id);
  const canEdit = isCoordinator || isContributor || user.role === "admin";

  const addMilestone = async () => {
    if (!newMs.title.trim()) return;
    const { data } = await api.post(`/deals/${id}/milestones`, { title: newMs.title, due_date: newMs.due_date || null });
    setDeal(data);
    setNewMs({ title: "", due_date: "" });
  };

  const toggleMs = async (msId) => {
    const { data } = await api.patch(`/deals/${id}/milestones/${msId}`);
    setDeal(data);
  };

  const saveNotes = async () => {
    setSaving(true);
    const payload = { group_notes: groupNotes };
    if (isCoordinator || user.role === "admin") payload.private_notes = privateNotes;
    const { data } = await api.patch(`/deals/${id}`, payload);
    setDeal(data);
    setSaving(false);
  };

  const changeStatus = async (s) => {
    const { data } = await api.patch(`/deals/${id}`, { status: s });
    setDeal(data);
  };

  const onDocUploaded = async () => { await load(); };
  const deleteDoc = async (fileId) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/uploads/files/${fileId}`);
    await load();
  };
  const docUrl = (fileId) =>
    `${API_BASE}/uploads/files/${fileId}`;
  const isImage = (ct) => (ct || "").startsWith("image/");

  return (
    <div className="space-y-8" data-testid="deal-detail-page">
      <Link to="/deals" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#9A9590] hover:text-[#C9A84C] transition">
        <ArrowLeft size={14} /> Back to deals
      </Link>

      <div className="ts-card p-8 sm:p-10">
        <div className="overline">{deal.client_name || "Confidential"}</div>
        <h1 className="font-serif text-3xl sm:text-5xl text-[#F0EDE8] mt-3 tracking-tight">{deal.name}</h1>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="overline">Value</div>
            <div className="font-serif text-2xl text-[#C9A84C] mt-1">{formatAED(deal.estimated_value)}</div>
          </div>
          <div>
            <div className="overline">Start</div>
            <div className="text-sm text-[#F0EDE8] mt-1">{formatDate(deal.start_date)}</div>
          </div>
          <div>
            <div className="overline">Target close</div>
            <div className="text-sm text-[#F0EDE8] mt-1">{formatDate(deal.target_close_date)}</div>
          </div>
          <div>
            <div className="overline">Status</div>
            {canEdit ? (
              <select
                value={deal.status}
                onChange={(e) => changeStatus(e.target.value)}
                data-testid="deal-status"
                className="mt-1 bg-[#1A1A1A] border border-[#C9A84C]/25 rounded-md px-2 py-1 text-sm text-[#F0EDE8] focus:outline-none focus:border-[#C9A84C]"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <div className="text-sm text-[#F0EDE8] mt-1 capitalize">{deal.status}</div>
            )}
          </div>
        </div>

        <p className="mt-6 text-[#F0EDE8] leading-relaxed">{deal.description}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="ts-card p-7 lg:col-span-2">
          <div className="overline mb-4">Milestones</div>
          <ul className="space-y-3" data-testid="milestones-list">
            {(deal.milestones || []).map((m) => (
              <li key={m.id} className="flex items-center gap-3 group">
                <button
                  onClick={() => isCoordinator || user.role === "admin" ? toggleMs(m.id) : null}
                  disabled={!(isCoordinator || user.role === "admin")}
                  className="shrink-0"
                  data-testid={`milestone-toggle-${m.id}`}
                >
                  {m.done ? <CheckCircle2 size={20} className="text-[#C9A84C]" /> : <Circle size={20} className="text-[#9A9590]" />}
                </button>
                <div className={`flex-1 ${m.done ? "line-through text-[#9A9590]" : "text-[#F0EDE8]"}`}>{m.title}</div>
                <div className="text-xs text-[#9A9590]">{formatDate(m.due_date)}</div>
              </li>
            ))}
            {(!deal.milestones || deal.milestones.length === 0) && (
              <li className="text-sm text-[#9A9590]">No milestones yet.</li>
            )}
          </ul>
          {(isCoordinator || user.role === "admin") && (
            <div className="mt-5 pt-5 border-t border-[#C9A84C]/12 flex flex-col sm:flex-row gap-2">
              <input className="ts-input flex-1" placeholder="New milestone" value={newMs.title} onChange={(e) => setNewMs({ ...newMs, title: e.target.value })} data-testid="milestone-title" />
              <input type="date" className="ts-input sm:w-44" value={newMs.due_date} onChange={(e) => setNewMs({ ...newMs, due_date: e.target.value })} />
              <button onClick={addMilestone} className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"><Plus size={14}/> Add</button>
            </div>
          )}
        </div>

        <div className="ts-card p-7">
          <div className="overline mb-3">Team</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar name={deal.coordinator?.name} photoUrl={deal.coordinator?.photo_url} color={deal.coordinator?.avatar_color} size={40} />
              <div>
                <div className="text-sm text-[#F0EDE8]">{deal.coordinator?.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#C9A84C]">Deal Coordinator</div>
              </div>
            </div>
            {(deal.contributors || []).map((c) => (
              <div key={c.id} className="flex items-center gap-3">
                <Avatar name={c.name} photoUrl={c.photo_url} color={c.avatar_color} size={36} />
                <div>
                  <div className="text-sm text-[#F0EDE8]">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#9A9590]">{c.sector}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="ts-card p-7" data-testid="deal-documents">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Documents</div>
            {canEdit && (
              <FileUpload
                endpoint={`/uploads/deal/${id}/document`}
                accept="application/pdf,image/jpeg,image/png,image/webp"
                maxBytes={10 * 1024 * 1024}
                variant="document"
                buttonLabel="Upload document"
                onUploaded={onDocUploaded}
                dataTestid="deal-doc-upload"
              />
            )}
          </div>
          <ul className="space-y-2" data-testid="deal-documents-list">
            {(deal.documents || []).map((d) => (
              <li key={d.file_id} className="flex items-center gap-3 p-3 rounded-md border border-[#C9A84C]/12 hover:border-[#C9A84C]/30 transition group">
                <div className="shrink-0 w-9 h-9 rounded bg-[#1A1A1A] border border-[#C9A84C]/20 flex items-center justify-center text-[#C9A84C]">
                  {isImage(d.content_type) ? <ImageIcon size={16} /> : <FileText size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F0EDE8] truncate">{d.original_filename}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#9A9590] mt-0.5">
                    {formatFileSize(d.size)} · {d.uploader?.name || "Unknown"} · {relativeTime(d.uploaded_at)}
                  </div>
                </div>
                <a href={docUrl(d.file_id)} target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:text-[#E1C97A] opacity-0 group-hover:opacity-100 transition" title="Open" data-testid={`doc-open-${d.file_id}`}>
                  <ExternalLink size={14} />
                </a>
                {(canEdit || user.role === "admin") && (
                  <button onClick={() => deleteDoc(d.file_id)} className="text-[#9A9590] hover:text-red-400 opacity-0 group-hover:opacity-100 transition" title="Remove" data-testid={`doc-delete-${d.file_id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
            {(!deal.documents || deal.documents.length === 0) && (
              <li className="text-sm text-[#9A9590]">No documents uploaded yet.</li>
            )}
          </ul>
        </div>

        <div className="ts-card p-7">
          <div className="overline mb-3">Group notes</div>
          <textarea
            value={groupNotes}
            onChange={(e) => setGroupNotes(e.target.value)}
            placeholder="Notes visible to contributors…"
            rows={6}
            disabled={!canEdit}
            className="ts-input resize-none py-2"
            data-testid="group-notes"
          />
        </div>
        <div className="ts-card p-7">
          <div className="overline mb-3">Private notes <span className="text-[#9A9590] normal-case tracking-normal">(coordinator)</span></div>
          <textarea
            value={privateNotes}
            onChange={(e) => setPrivateNotes(e.target.value)}
            placeholder={isCoordinator || user.role === "admin" ? "Private to coordinator…" : "Restricted — only the coordinator can view."}
            rows={6}
            disabled={!(isCoordinator || user.role === "admin")}
            className="ts-input resize-none py-2"
            data-testid="private-notes"
          />
        </div>
      </div>
      {canEdit && (
        <button onClick={saveNotes} disabled={saving} className="btn-gold rounded-md px-5 py-2.5 inline-flex items-center gap-2 text-sm" data-testid="save-notes">
          <Save size={14} /> {saving ? "Saving…" : "Save notes"}
        </button>
      )}

      <div className="ts-card p-7" data-testid="deal-discussion">
        <div className="overline mb-4">Discussion ({deal.comments?.length || 0})</div>
        <CommentThread
          apiPrefix={`/deals/${id}`}
          comments={deal.comments || []}
          currentUser={user}
          onUpdated={setDeal}
          canPost={canEdit}
          placeholder="Note for the deal team…"
          emptyHint="No comments yet — be the first to weigh in."
          forbiddenHint="Only the coordinator and contributors can comment on this deal."
        />
      </div>
    </div>
  );
}
